// OfficerEnrollDrawer — manage face / passkey bindings for an officer.
//
// On open, fetch current binding status from
//   GET /v1/admin/officers/:id/enroll-status
// then render two cards:
//
//   Face       — either 「已綁定 + 解除綁定 + 重新註冊連結」 OR 「產生註冊連結」
//   Passkeys   — list of bound devices, each with 「解除綁定」, plus a button to
//                add another device (產生新 passkey 註冊連結)
//
// Issuing a new token uses the existing
//   POST /v1/admin/officers/:id/face-enroll-token
//   POST /v1/admin/officers/:id/webauthn-enroll-token
// endpoints. Revokes use:
//   POST /v1/admin/officers/:id/face/revoke
//   POST /v1/admin/officers/:id/passkeys/:deviceId/revoke

import React, { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import {
  Smile, KeyRound, Copy, AlertCircle, CheckCircle2, Send, QrCode,
  Trash2, Smartphone, Plus, Loader2, RefreshCw,
} from "lucide-react";
import {
  issueOfficerFaceEnrollToken, issueOfficerWebauthnEnrollToken,
  getOfficerEnrollStatus, revokeOfficerFace, revokeOfficerPasskey,
  friendlyUsersError,
  type EnrollTokenResult, type OfficerEnrollStatus, type OfficerPasskeyRow,
} from "../lib/usersApi.js";
import { useConfirmDialog } from "./ConfirmDialog.js";
import { formatTaipeiDateTime } from "../../lib/time.js";

interface Props {
  officerId: string;
  officerName: string;
  tenantId: string | null;
}

type StatusState =
  | { kind: "loading" }
  | { kind: "ready"; data: OfficerEnrollStatus }
  | { kind: "error"; message: string };

export default function OfficerEnrollDrawer({ officerId, officerName, tenantId }: Props): React.ReactElement {
  const [status, setStatus] = useState<StatusState>({ kind: "loading" });

  const reload = useCallback(async () => {
    setStatus({ kind: "loading" });
    try {
      const data = await getOfficerEnrollStatus(officerId, tenantId);
      setStatus({ kind: "ready", data });
    } catch (e) {
      setStatus({ kind: "error", message: friendlyUsersError(e) });
    }
  }, [officerId, tenantId]);

  useEffect(() => { void reload(); }, [reload]);

  return (
    <div className="space-y-4">
      <p className="text-xs text-text-muted leading-relaxed">
        管理 <strong>{officerName}</strong> 的人臉與裝置綁定。註冊連結 24 小時內有效，使用後立刻失效。
      </p>

      {status.kind === "loading" && (
        <div className="card p-4 inline-flex items-center gap-2 text-xs text-text-muted">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> 讀取綁定狀態中…
        </div>
      )}

      {status.kind === "error" && (
        <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p>{status.message}</p>
            <button onClick={() => void reload()} className="text-2xs text-danger underline mt-1">重試</button>
          </div>
        </div>
      )}

      {status.kind === "ready" && (
        <>
          <FaceCard
            officerId={officerId}
            tenantId={tenantId}
            face={status.data.face}
            onChange={reload}
          />
          <PasskeysCard
            officerId={officerId}
            tenantId={tenantId}
            passkeys={status.data.passkeys}
            onChange={reload}
          />
        </>
      )}
    </div>
  );
}

// ── Face card ──────────────────────────────────────────────────────────

function FaceCard({ officerId, tenantId, face, onChange }: {
  officerId: string;
  tenantId: string | null;
  face: OfficerEnrollStatus["face"];
  onChange: () => Promise<void>;
}): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [token, setToken] = useState<EnrollTokenResult | null>(null);
  const dlg = useConfirmDialog();

  const issue = async () => {
    setBusy(true); setErr(null);
    try { setToken(await issueOfficerFaceEnrollToken(officerId, tenantId)); }
    catch (e) { setErr(friendlyUsersError(e)); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!(await dlg.confirm("確定要解除此使用者的人臉綁定？\n\n人臉特徵會被清除，下次執勤前需要重新註冊一次。", { destructive: true, okLabel: "解除綁定" }))) return;
    setBusy(true); setErr(null);
    try {
      await revokeOfficerFace(officerId, tenantId);
      setToken(null);
      await onChange();
    } catch (e) { setErr(friendlyUsersError(e)); }
    finally { setBusy(false); }
  };

  return (
    <section className="rounded-md border border-border bg-bg-raised p-4 space-y-3">
      <header className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold inline-flex items-center gap-2">
          <Smile className="h-4 w-4 text-accent" /> 人臉綁定
        </h3>
        {face.enrolled && (
          <span className="text-2xs px-2 py-0.5 rounded-full bg-success/15 text-success inline-flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> 已綁定
          </span>
        )}
      </header>

      {face.enrolled ? (
        <div className="rounded-md border border-border bg-bg-base p-3 space-y-2">
          <dl className="grid grid-cols-2 gap-y-1 text-xs">
            <dt className="text-text-muted">註冊時間</dt>
            <dd className="font-mono text-text-primary">
              {formatTaipeiDateTime(face.enrolled_at)}
            </dd>
            <dt className="text-text-muted">註冊 pose 數</dt>
            <dd className="font-mono text-text-primary">{face.pose_count}</dd>
          </dl>
        </div>
      ) : (
        <p className="text-xs text-text-secondary">
          開啟連結後手機自拍 5 個角度（正視 / 左轉 / 右轉 / 抬頭 / 低頭），系統建立人臉特徵向量。註冊一次。
        </p>
      )}

      {!token && (
        <div className="flex gap-2">
          {face.enrolled && (
            <button onClick={() => void revoke()} className="btn-danger text-xs flex-1" disabled={busy}>
              <Trash2 className="h-3.5 w-3.5" /> 解除綁定
            </button>
          )}
          <button onClick={() => void issue()} className="btn-primary text-xs flex-1" disabled={busy}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {face.enrolled ? "重新註冊連結" : "產生註冊連結"}
          </button>
        </div>
      )}

      {token && (
        <TokenPanel token={token} onClose={() => setToken(null)} onRefresh={() => { void issue(); }} busy={busy} />
      )}

      {err && (
        <div className="rounded border border-danger/40 bg-danger/10 p-2 text-xs text-danger flex items-start gap-2">
          <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
          <span>{err}</span>
        </div>
      )}
    </section>
  );
}

// ── Passkeys card ──────────────────────────────────────────────────────

function PasskeysCard({ officerId, tenantId, passkeys, onChange }: {
  officerId: string;
  tenantId: string | null;
  passkeys: OfficerPasskeyRow[];
  onChange: () => Promise<void>;
}): React.ReactElement {
  const activeKeys = passkeys.filter((p) => p.status === "active");
  const revokedKeys = passkeys.filter((p) => p.status === "revoked");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [token, setToken] = useState<EnrollTokenResult | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const dlg = useConfirmDialog();

  const issue = async () => {
    setBusy(true); setErr(null);
    try { setToken(await issueOfficerWebauthnEnrollToken(officerId, tenantId)); }
    catch (e) { setErr(friendlyUsersError(e)); }
    finally { setBusy(false); }
  };
  const revoke = async (deviceId: string, label: string) => {
    if (!(await dlg.confirm(`確定要解除「${label}」這個裝置的綁定？\n\n該裝置上的 Passkey 將立即失效，需要重新產生連結再綁定。`, { destructive: true, okLabel: "解除綁定" }))) return;
    setRevokingId(deviceId); setErr(null);
    try {
      await revokeOfficerPasskey(officerId, deviceId, tenantId);
      await onChange();
    } catch (e) { setErr(friendlyUsersError(e)); }
    finally { setRevokingId(null); }
  };

  return (
    <section className="rounded-md border border-border bg-bg-raised p-4 space-y-3">
      <header className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold inline-flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-accent" /> 裝置綁定（Passkey）
        </h3>
        {activeKeys.length > 0 && (
          <span className="text-2xs px-2 py-0.5 rounded-full bg-success/15 text-success inline-flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> {activeKeys.length} 個裝置已綁
          </span>
        )}
      </header>

      {activeKeys.length === 0 && (
        <p className="text-xs text-text-secondary">
          開啟連結後使用 Touch ID / Face ID / Windows Hello 完成 Passkey 綁定。任務級裝置驗證會用到。
        </p>
      )}

      {activeKeys.length > 0 && (
        <ul className="space-y-1.5">
          {activeKeys.map((d) => (
            <DeviceRow key={d.id} device={d} busy={revokingId === d.id} onRevoke={(label) => void revoke(d.id, label)} />
          ))}
        </ul>
      )}

      {revokedKeys.length > 0 && (
        <details className="text-xs">
          <summary className="text-text-muted cursor-pointer hover:text-text-primary">
            已停用（{revokedKeys.length}）
          </summary>
          <ul className="mt-2 space-y-1">
            {revokedKeys.map((d) => (
              <li key={d.id} className="px-2.5 py-1.5 rounded-lg bg-bg-base/50 text-2xs text-text-muted inline-flex items-center gap-2 w-full">
                <Smartphone className="h-3 w-3" />
                <span>{d.device_model ?? "未命名裝置"} · {d.os ?? "—"}</span>
                <span className="ml-auto">已停用</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {!token && (
        <button onClick={() => void issue()} className="btn-primary text-xs w-full" disabled={busy}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          {activeKeys.length === 0 ? "產生註冊連結" : "新增裝置（產生連結）"}
        </button>
      )}

      {token && (
        <TokenPanel token={token} onClose={() => setToken(null)} onRefresh={() => { void issue(); }} busy={busy} />
      )}

      {err && (
        <div className="rounded border border-danger/40 bg-danger/10 p-2 text-xs text-danger flex items-start gap-2">
          <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
          <span>{err}</span>
        </div>
      )}
    </section>
  );
}

function DeviceRow({ device, busy, onRevoke }: {
  device: OfficerPasskeyRow;
  busy: boolean;
  onRevoke: (label: string) => void;
}): React.ReactElement {
  const label = device.device_model ?? "未命名裝置";
  const lastSeen = device.last_seen_at ? formatTaipeiDateTime(device.last_seen_at) : null;
  const enrolledLocal = formatTaipeiDateTime(device.enrolled_at);
  return (
    <li className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-bg-base border border-border-subtle">
      <Smartphone className="h-4 w-4 text-text-muted flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{label}</div>
        <div className="text-2xs text-text-muted truncate">
          {device.os ?? "—"} · 註冊 {enrolledLocal}
          {lastSeen && ` · 最近 ${lastSeen}`}
        </div>
      </div>
      <button
        onClick={() => onRevoke(label)}
        disabled={busy}
        className="text-2xs px-2 py-1 rounded text-danger hover:bg-danger/10 inline-flex items-center gap-1"
        aria-label="解除綁定"
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
        解除
      </button>
    </li>
  );
}

// ── Shared token panel (QR + URL + share) ──────────────────────────────

function TokenPanel({ token, onClose, onRefresh, busy }: {
  token: EnrollTokenResult;
  onClose: () => void;
  onRefresh: () => void;
  busy: boolean;
}): React.ReactElement {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const qrLockRef = useRef<string | null>(null);
  useEffect(() => {
    if (qrLockRef.current === token.web_url) return;
    qrLockRef.current = token.web_url;
    QRCode.toDataURL(token.web_url, {
      errorCorrectionLevel: "M", margin: 1, width: 256,
      color: { dark: "#0a0a0c", light: "#ffffff" },
    }).then(setQrDataUrl).catch(() => setQrDataUrl(null));
  }, [token.web_url]);
  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
      () => { /* clipboard refused */ },
    );
  };
  const lineShare = `https://line.me/R/msg/text/?${encodeURIComponent(`${token.kind === "face" ? "人臉" : "Passkey"}註冊連結（24h 內有效）\n${token.web_url}`)}`;
  return (
    <div className="space-y-3">
      {token.reused && (
        <p className="text-2xs text-text-muted">
          重用既有未過期連結（剩餘 {Math.floor(token.expires_in_seconds / 60)} 分）。
        </p>
      )}
      {qrDataUrl && (
        <div className="rounded-md border border-border bg-white p-3 flex flex-col items-center gap-2">
          <img src={qrDataUrl} alt="QR code 註冊連結" className="w-44 h-44" />
          <p className="text-2xs text-text-inverse inline-flex items-center gap-1">
            <QrCode className="h-3 w-3" /> 用對方手機相機掃描
          </p>
        </div>
      )}
      <div className="rounded-md border border-accent/30 bg-accent-subtle p-2 text-xs font-mono break-all select-all">
        {token.web_url}
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => copy(token.web_url)}
          className="btn-secondary text-xs flex-1 inline-flex items-center justify-center gap-1"
        >
          <Copy className="h-3 w-3" /> {copied ? "已複製" : "複製連結"}
        </button>
        <a
          href={lineShare}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary text-xs flex-1 inline-flex items-center justify-center gap-1"
        >
          <Send className="h-3 w-3" /> 透過 LINE 傳送
        </a>
      </div>
      <div className="flex gap-2">
        <button onClick={onRefresh} disabled={busy} className="text-2xs text-accent hover:text-accent-hover flex-1">
          {busy ? "重新產生中…" : "重新產生"}
        </button>
        <button onClick={onClose} className="text-2xs text-text-muted hover:text-text-primary flex-1">
          收合
        </button>
      </div>
    </div>
  );
}
